import { CSSInterpolation } from '@emotion/serialize'
import Downshift, { DownshiftProps } from 'downshift'
import { hideVisually } from 'polished'
import PropTypes from 'prop-types'
import React, { Fragment, useMemo } from 'react'

import svgChevronDownUrl from 'assets/iconsNew/chevron-down.svg'
import Button from 'components/common/Button'
import FormSubtext from 'components/common/FormSubtext'
import Input from 'components/common/Input'
import StyledCheckmark from 'components/common/StyledCheckmark'
import Text, { TextProps } from 'components/common/Text'
import TextButton from 'components/common/TextButton'
import theme from 'theme'

const arrowSize = theme.space[3]
const getArrowStyles = (arrowLeft): CSSInterpolation => {
  return {
    '&::before': arrowLeft
      ? {
          content: '""',
          backgroundImage: `url(${svgChevronDownUrl})`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center left',
          position: 'relative',
          left: 0,
          width: arrowSize + theme.space[3],
          height: arrowSize,
          pointerEvents: 'none',
          zIndex: 1
        }
      : undefined,
    '&::after': !arrowLeft
      ? {
          content: '""',
          backgroundImage: `url(${svgChevronDownUrl})`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center center',
          position: 'absolute',
          right: theme.space[3],
          width: arrowSize,
          height: arrowSize,
          pointerEvents: 'none',
          zIndex: 1
        }
      : undefined
  }
}

type StyledDropdownInputProps = {
  isOpen?: boolean
  hideArrow?: boolean
  noLeftRadius?: boolean
  noRightRadius?: boolean
  arrowLeft?: boolean
}

const StyledDropdownInput = React.forwardRef<
  React.RefObject<HTMLInputElement>,
  StyledDropdownInputProps
>(
  (
    { isOpen, hideArrow, noLeftRadius, noRightRadius, arrowLeft, ...rest },
    ref
  ) => {
    const arrowStyles = getArrowStyles(arrowLeft)
    return (
      <Input
        ref={ref}
        css={
          [
            {
              borderTopLeftRadius: noLeftRadius ? 0 : theme.borderRadius[0],
              borderTopRightRadius: noRightRadius ? 0 : theme.borderRadius[0],
              borderBottomLeftRadius: (isOpen || noLeftRadius) && 0,
              borderBottomRightRadius: (isOpen || noRightRadius) && 0,
              borderBottomColor: theme.colors.border[0],
              '& > input': {
                textOverflow: 'ellipsis',
                marginRight: hideArrow ? 0 : theme.space[3]
              }
            },

            !hideArrow && (arrowStyles as any),

            isOpen && {
              borderColor: theme.colors.blue[0]
            }
          ] as Array<CSSInterpolation>
        }
        {...rest}
      />
    )
  }
)

StyledDropdownInput.displayName = 'StyledDropdownInput'

function RenderItem({
  item,
  index,
  renderItem,
  getItemProps,
  highlightedIndex,
  selectedValue,
  isOpen,
  toggleMenu
}) {
  const isHighlighted = index === highlightedIndex
  const isSelected = item.value === selectedValue

  // renderItem is given a chance to custom render the menu item
  if (typeof renderItem === 'function') {
    const renderResult = renderItem({
      item,
      index,
      itemProps: getItemProps({ item, index }),
      isSelected,
      isHighlighted,
      isMenuOpen: isOpen,
      highlightedIndex,
      selectedValue,
      toggleMenu
    })

    // you can return false to let the item be rendered normally as a DropdownItem below
    // this way if there is logic in the parent component that controls which items to custom-render
    // then it can let this component render a DropdownItem so it doesn't have to import that component and render it
    if (renderResult !== false && renderResult !== null) {
      return renderResult
    }
  }

  return (
    <DropdownItem
      getItemProps={getItemProps}
      highlightedIndex={highlightedIndex}
      index={index}
      item={item}
      selectedValue={selectedValue}
    />
  )
}

// note: this component's prop typings are reused
type DropdownProps = React.HTMLAttributes<HTMLElement> & {
  className?: string
  selectedValue?: string | number
  placeholder?: string
  emptyText?: string
  name?: string
  items?: Array<any>
  onSelected(any, event: React.ChangeEvent<HTMLInputElement>): void
  renderToggleButton?: (props: Record<string, any>) => any
  inputValue?: string
  searchable?: boolean
  onChange?: (ev: React.FormEvent<HTMLInputElement>) => void
  showClear?: boolean
  showLabelOnValue?: boolean
  buttonStyles?: CSSInterpolation
  inputStyles?:
    | CSSInterpolation
    | ((downshiftProps: DownshiftProps<any>) => CSSInterpolation)
  menuStyles?: CSSInterpolation
  hideArrow?: boolean
  arrowLeft?: boolean
  noLeftRadius?: boolean
  noRightRadius?: boolean
  disabled?: boolean
  disabledText?: string
  renderItem?: (any) => React.ReactNode
  displayValue?: string
  displayValuePrefix?: React.ReactNode
  itemToString?: (any: any) => string
  isInvalid?: boolean
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>
  stateReducer?: (
    state: Record<string, any>,
    changes: Record<string, any>
  ) => any
  [x: string]: any
}

const Dropdown: React.FC<DropdownProps> = ({
  className,
  items = [],
  selectedValue,
  inputValue,
  emptyText,
  placeholder,
  onSelected,
  renderToggleButton,
  searchable,
  onChange,
  showClear,
  showLabelOnValue,
  buttonStyles,
  inputStyles,
  menuStyles,
  hideArrow,
  noLeftRadius,
  noRightRadius,
  disabled,
  disabledText,
  itemToString = (i) => i?.value,
  isInvalid,
  arrowLeft,
  renderItem,
  displayValue,
  displayValuePrefix,
  onKeyDown,
  stateReducer,
  ...props
}) => {
  const analytics = props['data-track-on']
    ? {
        'data-track-on': props['data-track-on'],
        'data-track-category': props['data-track-category'],
        'data-track-action': props['data-track-action']
      }
    : {}

  const selectedItem = items.find((i) => selectedValue === i.value) || {}
  const { label: selectedLabel } = selectedItem
  const ungroupedItems = []
  const groups = {}

  const hasSearchTerm =
    inputValue && typeof inputValue === 'string' && inputValue.length

  const filteredItems = useMemo(
    () =>
      hasSearchTerm
        ? items.filter(({ label, group } = {}) => {
            const searchTerm = inputValue.toLowerCase()
            const labelValue = typeof label === 'string' ? label : label.value

            if (
              typeof group === 'string' &&
              group.toLowerCase().includes(searchTerm)
            ) {
              return true
            }

            if (labelValue) {
              return labelValue.toLowerCase().includes(searchTerm)
            }

            return true
          })
        : items,
    [hasSearchTerm, inputValue, items]
  )

  filteredItems.forEach((item) => {
    // push item depending on whether it is
    // the selected value and use wants to omit it
    // or the user wants to see the full list of items always
    if (item.group) {
      if (!groups[item.group]) groups[item.group] = []
      groups[item.group].push(item)
    } else {
      ungroupedItems.push(item)
    }
  })

  const arrowStyles = getArrowStyles(arrowLeft)

  return (
    <Downshift
      itemToString={itemToString}
      stateReducer={stateReducer}
      onChange={(event) =>
        event && onSelected && onSelected(event.value, event)
      }
    >
      {(downshiftProps) => {
        const {
          getToggleButtonProps,
          getInputProps,
          getItemProps,
          getRootProps,
          isOpen,
          toggleMenu,
          highlightedIndex,
          setHighlightedIndex,
          getMenuProps
        } = downshiftProps

        let groupedItemIndex = 0
        const customInputProps = {
          onChange: (event) => {
            setHighlightedIndex(0)
            onChange(event)
          },
          prefix: !isOpen && displayValuePrefix,
          onBlur: props.onBlur,
          value: isOpen
            ? inputValue
            : displayValue || selectedLabel || selectedValue || '',
          onKeyDown: onKeyDown
        }

        const customStyles = [
          isInvalid && {
            '&:focus': {
              borderColor: theme.colors.red[0]
            }
          },
          {
            '& input': {
              cursor: disabled ? 'not-allowed' : 'pointer'
            },
            ...(disabled && { opacity: theme.opacity[0] }),
            ...(Boolean(showLabelOnValue) &&
              !isOpen &&
              Boolean(selectedValue) && {
                '& input': {
                  paddingTop: theme.space[2]
                }
              })
          },
          typeof inputStyles === 'function'
            ? inputStyles(downshiftProps)
            : inputStyles
        ]

        const inputProps = {
          arrowLeft: arrowLeft,
          'data-test-dropdown-input': props.name,
          hideArrow,
          name: props.name,
          noLeftRadius: noLeftRadius,
          noRightRadius: noRightRadius,
          readOnly: !isOpen,
          onClick: disabled ? () => true : () => toggleMenu(),
          ...getInputProps(customInputProps),
          css: customStyles,
          disableFocusIndicator: true,
          isOpen: isOpen,
          placeholder: disabled ? disabledText || placeholder : placeholder,
          suffix: (
            <RightPanel
              selectedValue={selectedValue}
              showClear={showClear}
              onSelected={onSelected}
            />
          )
        }

        return (
          <div
            {...getRootProps()}
            data-test-dropdown
            className={className}
            css={{
              position: 'relative',
              marginLeft: noLeftRadius && -1,
              marginRight: noRightRadius && 0
            }}
            data-test-dropdown-disabled={disabled}
            data-test-field-value-name={props.name}
            data-testid="dropdown"
          >
            {(renderToggleButton && renderToggleButton(downshiftProps)) ||
              (searchable ? (
                <Fragment>
                  {Boolean(showLabelOnValue) &&
                    !isOpen &&
                    Boolean(selectedValue) && (
                      <Text
                        as="small"
                        css={{
                          opacity: theme.opacity[0],
                          position: 'absolute',
                          marginLeft: theme.space[3],
                          transform: 'translateY(-50%)',
                          top: '50%',
                          marginTop: -8
                        }}
                        weight="normal"
                      >
                        {placeholder}
                      </Text>
                    )}
                  <StyledDropdownInput {...inputProps} />
                </Fragment>
              ) : (
                <DropdownButton
                  {...getToggleButtonProps()}
                  arrowLeft={arrowLeft}
                  css={
                    [
                      isInvalid && {
                        '&:focus': {
                          borderColor: theme.colors.red[0]
                        }
                      },
                      {
                        '& input': {
                          cursor: disabled ? 'not-allowed' : 'pointer'
                        }
                      },
                      disabled && { opacity: theme.opacity[0] },
                      !hideArrow && (arrowStyles as CSSInterpolation),
                      buttonStyles
                    ] as CSSInterpolation[]
                  }
                  disabled={disabled}
                  hideArrow={hideArrow}
                  isOpen={isOpen}
                  noLeftRadius={noLeftRadius}
                  noRightRadius={noRightRadius}
                  {...analytics}
                >
                  <ButtonLabel
                    isInvalid={isInvalid}
                    label={
                      typeof selectedLabel === 'string'
                        ? selectedLabel
                        : selectedLabel?.value
                    }
                    placeholder={placeholder}
                    showLabelOnValue={Boolean(showLabelOnValue)}
                  />
                  <RightPanel
                    selectedValue={selectedValue}
                    showClear={showClear}
                    onSelected={onSelected}
                  />
                </DropdownButton>
              ))}
            <Menu
              isOpen={isOpen}
              {...getMenuProps()}
              css={[
                noRightRadius ? { paddingTop: 0, paddingBottom: 0 } : null,
                menuStyles
              ]}
            >
              {isOpen && (
                <Fragment>
                  {!filteredItems.length && (
                    <Item as="div" color="light" css={{ cursor: 'default' }}>
                      {emptyText || 'No items found.'}
                    </Item>
                  )}
                  {Boolean(filteredItems.length) &&
                    Object.keys(groups).map((groupName) => (
                      <div key={groupName}>
                        <Text
                          as="small"
                          css={{
                            textTransform: 'uppercase',
                            marginBottom: theme.space[2],
                            marginLeft: theme.space[2],
                            marginTop: theme.space[2],
                            fontWeight: theme.fontWeights[1]
                          }}
                          weight="normal"
                        >
                          {groupName}
                        </Text>
                        {groups[groupName].map((item) => (
                          <RenderItem
                            key={itemToString(item)}
                            getItemProps={getItemProps}
                            highlightedIndex={highlightedIndex}
                            index={groupedItemIndex++}
                            isOpen={isOpen}
                            item={item}
                            renderItem={renderItem}
                            selectedValue={selectedValue}
                            toggleMenu={toggleMenu}
                          />
                        ))}
                      </div>
                    ))}
                  {Boolean(filteredItems.length) &&
                    ungroupedItems.map((item, index) => (
                      <RenderItem
                        key={itemToString(item)}
                        getItemProps={getItemProps}
                        highlightedIndex={highlightedIndex}
                        index={groupedItemIndex + index}
                        isOpen={isOpen}
                        item={item}
                        renderItem={renderItem}
                        selectedValue={selectedValue}
                        toggleMenu={toggleMenu}
                      />
                    ))}
                </Fragment>
              )}
            </Menu>
          </div>
        )
      }}
    </Downshift>
  )
}

export default Dropdown

function RightPanel({ onSelected, showClear, selectedValue }) {
  return showClear && selectedValue ? (
    <div
      css={{
        display: 'flex',
        alignItems: 'center',
        marginLeft: 'right'
      }}
    >
      <TextButton
        dark
        css={{
          fontSize: theme.fontSizes[0],
          paddingRight: `calc(${theme.space[2]}px + ${arrowSize}px)`,
          minHeight: 48
        }}
        onClick={(event) => {
          event.stopPropagation()
          onSelected && onSelected(null)
        }}
      >
        Clear
      </TextButton>
    </div>
  ) : null
}

RightPanel.propTypes = {
  onSelected: PropTypes.func,
  showClear: PropTypes.bool,
  className: PropTypes.string,
  selectedValue: PropTypes.any
}

type DropdownItemProps = {
  getItemProps?: (any) => any
  item: Record<any, any>
  selectedValue: string
  index?: number
  highlightedIndex?: number
  renderItem?: (any) => any
}

export const DropdownItem = React.forwardRef<React.Ref<any>, DropdownItemProps>(
  (
    { getItemProps, item, index, highlightedIndex, selectedValue, renderItem },
    ref: React.Ref<any>
  ) => {
    const { label, value } = item
    let labelValue
    let labelContent

    if (typeof label === 'string') {
      labelValue = label
      labelContent = label
    } else {
      labelValue = label.value
      labelContent = label.content
    }

    if (renderItem) {
      return renderItem(item)
    }

    return (
      <Item
        ref={ref}
        data-test-dropdown-item={labelValue}
        {...getItemProps({ item, index })}
        overflowEllipsis
        as="div"
        hasCustomRender={Boolean(renderItem)}
        isHighlighted={index === highlightedIndex}
        isSelected={value === selectedValue}
      >
        {labelContent}
      </Item>
    )
  }
)

DropdownItem.displayName = 'DropdownItem'

function ButtonLabel({ label, placeholder, showLabelOnValue, isInvalid }) {
  if (label && showLabelOnValue) {
    return (
      <div css={{ '& *': { fontWeight: theme.fontWeights[0] } }}>
        <Text as="small" css={{ opacity: theme.opacity[0] }} weight="normal">
          {placeholder}
        </Text>
        <Text
          as="p"
          css={{
            fontSize: theme.fontSizes[2],
            color: theme.colors.steel[2]
          }}
          weight="normal"
        >
          {label}
        </Text>
      </div>
    )
  }

  if (!label)
    return (
      <span
        css={{
          color: isInvalid ? theme.colors.red[0] : theme.colors.steel[1],
          fontWeight: theme.fontWeights[0],
          opacity: theme.opacity[0]
        }}
      >
        {placeholder || '(None)'}
      </span>
    )

  return <span css={{ fontWeight: theme.fontWeights[0] }}>{label}</span>
}

ButtonLabel.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  placeholder: PropTypes.string,
  showLabelOnValue: PropTypes.bool,
  isInvalid: PropTypes.bool
}

type MenuProps = {
  isOpen?: boolean
}

export const Menu = React.forwardRef<
  React.Ref<HTMLUListElement> | string,
  MenuProps
>(({ isOpen, ...rest }, ref: React.Ref<any>) => {
  return (
    <ul
      ref={ref}
      css={[
        !isOpen && hideVisually(),

        {
          borderBottom: theme.border.base,
          paddingTop: theme.space[2],
          paddingBottom: theme.space[2],
          position: 'absolute',
          backgroundColor: theme.colors.white[0],
          width: '100%',
          zIndex: theme.zIndices[1],
          maxHeight: 300,
          overflowY: 'scroll',
          textAlign: 'left',
          listStyle: 'none',
          borderColor: theme.colors.gray[1],
          boxShadow: theme.boxShadows.large
        }
      ]}
      {...rest}
    />
  )
})

Menu.displayName = 'Menu'

type ItemProps = {
  isSelected?: boolean
  isHighlighted?: boolean
  hasCustomRender?: boolean
}

const Item = React.forwardRef<React.Ref<any>, ItemProps & TextProps>(
  (
    { isSelected, isHighlighted, children, hasCustomRender, ...props },
    ref: React.Ref<any>
  ) => (
    <Text
      ref={ref}
      css={[
        {
          paddingLeft: theme.space[2],
          paddingRight: theme.space[2],
          paddingBottom: theme.space[2],
          paddingTop: theme.space[2],
          cursor: 'pointer'
        },
        isHighlighted && {
          backgroundColor: theme.colors.panel[0]
        }
      ]}
      {...props}
    >
      <span
        css={[
          {
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) auto',
            gap: theme.space[2],
            alignItems: 'center',
            fontWeight: theme.fontWeights[0],
            color: theme.colors.steel[2],
            fontSize: theme.fontSizes[1]
          },
          isSelected && {
            fontWeight: theme.fontWeights[1]
          },
          isHighlighted && {
            color: theme.colors.steel[2]
          }
        ]}
      >
        <div
          css={[
            !hasCustomRender && {
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }
          ]}
        >
          {children}
        </div>
        {isSelected && <StyledCheckmark css={{ justifySelf: 'end' }} />}
      </span>
    </Text>
  )
)

Item.displayName = 'Item'

type DropdownButtonProps = {
  isOpen: boolean
  hideArrow: boolean
  noLeftRadius: boolean
  noRightRadius: boolean
  arrowLeft: boolean
}

const DropdownButton = React.forwardRef<React.Ref<any>, DropdownButtonProps>(
  (
    { isOpen, hideArrow, noLeftRadius, noRightRadius, arrowLeft, ...rest },
    ref
  ) => {
    const arrowStyles = getArrowStyles(arrowLeft)
    return (
      <Button
        ref={ref}
        css={
          [
            {
              borderTopRightRadius: noRightRadius ? 0 : theme.borderRadius[0],
              borderTopLeftRadius: noLeftRadius ? 0 : theme.borderRadius[0],
              borderBottomLeftRadius:
                isOpen || noLeftRadius ? 0 : theme.borderRadius[0],
              borderBottomRightRadius:
                isOpen || noRightRadius ? 0 : theme.borderRadius[0],
              fontSize: theme.fontSizes[2],
              width: '100%',
              textAlign: 'left',
              border: theme.border.base,
              paddingLeft: theme.space[2],
              paddingRight: theme.space[2],
              cursor: 'pointer',
              display: 'flex',
              position: 'relative',
              alignItems: 'center',
              color: theme.colors.steel[2],
              backgroundColor: theme.colors.white[0],
              minHeight: 48,
              zIndex: noRightRadius ? theme.zIndices[0] : undefined,

              '&:focus': {
                zIndex: noRightRadius ? theme.zIndices[2] : undefined,
                border: '1px solid',
                borderColor: theme.colors.blue[0]
              },

              '&:disabled': {
                background: theme.colors.white[0]
              },

              '&:hover, &:focus': {
                color: theme.colors.steel[2],
                background: theme.colors.white[0]
              }
            },

            !hideArrow && arrowStyles,

            isOpen && {
              borderColor: theme.colors.blue[0]
            }
          ] as CSSInterpolation[]
        }
        {...rest}
      />
    )
  }
)

DropdownButton.displayName = 'DropdownButton'

type DropdownWithSubTextProps = DropdownProps & {
  isInvalid?: boolean
  subtext?: React.ReactNode
}

export const DropdownWithSubtext: React.FC<DropdownWithSubTextProps> = ({
  isInvalid,
  subtext,
  ...rest
}) => {
  return (
    <Fragment>
      <Dropdown {...rest} isInvalid={isInvalid} />
      <FormSubtext isInvalid={isInvalid}>{subtext}</FormSubtext>
    </Fragment>
  )
}
